import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTeam } from './TeamContext';
import { useTCoins, TCOINS_TYPES } from './TCoinsContext';
import { useNotification } from './NotificationContext';
import {
  getReceivedGifts,
  getSentGifts,
  getTeamGifts,
  giveGift as giveGiftUtil,
  calculateGiftStats,
  getTopGiftReceivers,
  getTopGiftGivers,
  GIFTS,
  GIFT_CONTEXTS
} from '../utils/nurseGiftManager';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

const GiftContext = createContext();

export const useGift = () => {
  const context = useContext(GiftContext);
  if (!context) {
    throw new Error('useGift must be used within GiftProvider');
  }
  return context;
};

export const GiftProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { deductTCoins, addTCoins, getTotalTCoins, tcoins } = useTCoins();
  const { createNotification } = useNotification();
  
  const [receivedGifts, setReceivedGifts] = useState([]);
  const [sentGifts, setSentGifts] = useState([]);
  const [teamGifts, setTeamGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load received gifts
  useEffect(() => {
    if (!currentUser) {
      setReceivedGifts([]);
      setLoading(false);
      return;
    }

    const loadReceivedGifts = async () => {
      try {
        const gifts = await getReceivedGifts(currentUser.uid, currentTeam?.id);
        setReceivedGifts(gifts);
      } catch (err) {
        console.error('Error loading received gifts:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadReceivedGifts();

    // Real-time subscription
    let unsubscribe = null;
    try {
      const constraints = [
        where('toUserId', '==', currentUser.uid)
      ];
      
      if (currentTeam?.id) {
        constraints.push(where('teamId', '==', currentTeam.id));
      }
      
      constraints.push(orderBy('timestamp', 'desc'));
      
      const q = query(
        collection(db, 'gifts'),
        ...constraints
      );
      
      unsubscribe = onSnapshot(q, (snapshot) => {
        const gifts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setReceivedGifts(gifts);
        setLoading(false);
      }, (error) => {
        console.error('Error in received gifts subscription:', error);
        // Fallback: load without real-time if subscription fails
        loadReceivedGifts();
      });
    } catch (error) {
      console.error('Error setting up received gifts subscription:', error);
      // Fallback: load without real-time if subscription setup fails
      loadReceivedGifts();
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser, currentTeam]);

  // Load sent gifts
  useEffect(() => {
    if (!currentUser) {
      setSentGifts([]);
      return;
    }

    const loadSentGifts = async () => {
      try {
        const gifts = await getSentGifts(currentUser.uid, currentTeam?.id);
        setSentGifts(gifts);
      } catch (err) {
        console.error('Error loading sent gifts:', err);
      }
    };

    loadSentGifts();

    // Real-time subscription
    let unsubscribe = null;
    try {
      const constraints = [
        where('fromUserId', '==', currentUser.uid)
      ];
      
      if (currentTeam?.id) {
        constraints.push(where('teamId', '==', currentTeam.id));
      }
      
      constraints.push(orderBy('timestamp', 'desc'));
      
      const q = query(
        collection(db, 'gifts'),
        ...constraints
      );
      
      unsubscribe = onSnapshot(q, (snapshot) => {
        const gifts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSentGifts(gifts);
      }, (error) => {
        console.error('Error in sent gifts subscription:', error);
        // Fallback: load without real-time if subscription fails
        loadSentGifts();
      });
    } catch (error) {
      console.error('Error setting up sent gifts subscription:', error);
      // Fallback: load without real-time if subscription setup fails
      loadSentGifts();
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser, currentTeam]);

  // Load team gifts
  useEffect(() => {
    if (!currentTeam) {
      setTeamGifts([]);
      return;
    }

    const loadTeamGifts = async () => {
      try {
        const gifts = await getTeamGifts(currentTeam.id);
        setTeamGifts(gifts);
      } catch (err) {
        console.error('Error loading team gifts:', err);
        setTeamGifts([]);
      }
    };

    loadTeamGifts();

    // Real-time subscription - with error handling
    let unsubscribe = null;
    try {
      const q = query(
        collection(db, 'gifts'),
        where('teamId', '==', currentTeam.id),
        orderBy('timestamp', 'desc')
      );
      
      unsubscribe = onSnapshot(q, (snapshot) => {
        const gifts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTeamGifts(gifts);
      }, (err) => {
        console.error('Error loading team gifts (realtime):', err);
        // If index error, use fallback query without orderBy
        if (err.code === 'failed-precondition' || err.message?.includes('index')) {
          const fallbackQ = query(
            collection(db, 'gifts'),
            where('teamId', '==', currentTeam.id)
          );
          unsubscribe = onSnapshot(fallbackQ, (snapshot) => {
            const gifts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Sort manually
            gifts.sort((a, b) => {
              const aTime = a.timestamp?.toMillis?.() || 0;
              const bTime = b.timestamp?.toMillis?.() || 0;
              return bTime - aTime;
            });
            setTeamGifts(gifts);
          });
        }
      });
    } catch (err) {
      console.error('Error setting up team gifts subscription:', err);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentTeam]);

  /**
   * ให้ของขวัญ
   * @param {Object} giftData - ข้อมูลของขวัญ
   */
  const giveGift = async (giftData) => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const giftDef = GIFTS.find(g => g.id === giftData.giftType);
      if (!giftDef) {
        throw new Error('ไม่พบประเภทของขวัญ');
      }

      // ตรวจสอบ T Coins
      const totalTCoins = getTotalTCoins();
      if (totalTCoins < giftDef.price) {
        throw new Error('T Coins ไม่เพียงพอ');
      }

      // หัก T Coins จากผู้ให้ (ใช้ real coins ก่อน ถ้าไม่พอให้ใช้ virtual)
      let remainingPrice = giftDef.price;
      const realCoins = tcoins.real || 0;
      
      // หัก real coins ก่อน
      if (realCoins >= remainingPrice) {
        await deductTCoins(
          currentUser.uid,
          TCOINS_TYPES.REAL,
          remainingPrice,
          `ให้ของขวัญ ${giftDef.name} ให้ ${giftData.toUserName}`
        );
        remainingPrice = 0;
      } else {
        // หัก real coins ที่มี
        if (realCoins > 0) {
          await deductTCoins(
            currentUser.uid,
            TCOINS_TYPES.REAL,
            realCoins,
            `ให้ของขวัญ ${giftDef.name} ให้ ${giftData.toUserName}`
          );
          remainingPrice -= realCoins;
        }
        
        // หัก virtual coins ที่เหลือ
        if (remainingPrice > 0) {
          await deductTCoins(
            currentUser.uid,
            TCOINS_TYPES.VIRTUAL,
            remainingPrice,
            `ให้ของขวัญ ${giftDef.name} ให้ ${giftData.toUserName}`
          );
        }
      }

      // เพิ่ม T Coins ให้ผู้รับ (ใช้ real coins)
      await addTCoins(
        giftData.toUserId,
        TCOINS_TYPES.REAL,
        giftDef.price,
        `ได้รับของขวัญ ${giftDef.name} จาก ${currentUser.displayName || currentUser.email}`
      );

      // ดึง role ของผู้รับจาก Firestore
      let toUserRole = giftData.toUserRole;
      if (!toUserRole && giftData.toUserId) {
        try {
          const toUserRef = doc(db, 'users', giftData.toUserId);
          const toUserSnap = await getDoc(toUserRef);
          if (toUserSnap.exists()) {
            const toUserData = toUserSnap.data();
            toUserRole = toUserData.role || toUserData.jobRole || 'MEMBER';
          } else {
            toUserRole = 'MEMBER'; // Fallback if user not found
          }
        } catch (err) {
          console.error('Error fetching toUser role:', err);
          toUserRole = 'MEMBER'; // Fallback
        }
      }
      
      // ตรวจสอบอีกครั้งเพื่อให้แน่ใจว่าไม่เป็น undefined หรือ null
      if (!toUserRole) {
        toUserRole = 'MEMBER';
      }

      // บันทึกการให้ของขวัญ
      const gift = await giveGiftUtil({
        ...giftData,
        fromUserId: currentUser.uid,
        fromUserName: currentUser.displayName || currentUser.email,
        fromUserRole: currentTeam?.userRole || 'MEMBER',
        toUserRole: toUserRole,
        teamId: currentTeam?.id
      });

      // ส่ง notification ให้ผู้รับ
      await createNotification(
        giftData.toUserId,
        'gift_received',
        `ได้รับของขวัญ ${giftDef.name}`,
        `${currentUser.displayName || currentUser.email} ให้${giftDef.name}คุณ`,
        {
          giftId: gift.giftId,
          giftType: gift.giftType,
          fromUserId: currentUser.uid,
          fromUserName: currentUser.displayName || currentUser.email
        }
      );

      return { success: true, gift };
    } catch (err) {
      console.error('Error giving gift:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * คำนวณสถิติของขวัญที่ได้รับ
   */
  const getReceivedGiftStats = () => {
    return calculateGiftStats(receivedGifts);
  };

  /**
   * คำนวณสถิติของขวัญที่ให้
   */
  const getSentGiftStats = () => {
    return calculateGiftStats(sentGifts);
  };

  /**
   * ดึง top receivers ในทีม
   */
  const getTopReceivers = async (limit = 10) => {
    if (!currentTeam) return [];
    try {
      return await getTopGiftReceivers(currentTeam.id, limit);
    } catch (err) {
      console.error('Error getting top receivers:', err);
      return [];
    }
  };

  /**
   * ดึง top givers ในทีม
   */
  const getTopGivers = async (limit = 10) => {
    if (!currentTeam) return [];
    try {
      return await getTopGiftGivers(currentTeam.id, limit);
    } catch (err) {
      console.error('Error getting top givers:', err);
      return [];
    }
  };

  const value = {
    // Gifts
    receivedGifts,
    sentGifts,
    teamGifts,
    
    // Stats
    getReceivedGiftStats,
    getSentGiftStats,
    getTopReceivers,
    getTopGivers,
    
    // Actions
    giveGift,
    
    // Constants
    GIFTS,
    GIFT_CONTEXTS,
    
    // State
    loading,
    error
  };

  return (
    <GiftContext.Provider value={value}>
      {children}
    </GiftContext.Provider>
  );
};

